import type {TextProjection} from './annotationTextProjection';
import type {AnnotationRange, TextAnchor} from './annotationTypes';

/** Context captured around `exact` for the deferred self-heal search. */
const PREFIX_SUFFIX_LENGTH = 32;

/**
 * Builds the frozen v1 anchor for a normalized range. Null when out of bounds
 * or whitespace-only (a drag over just a collapsed newline is not a mark).
 */
export function buildAnchor(
  projection: TextProjection,
  {start, end}: AnnotationRange,
  {regionId, lang}: {regionId: string; lang: string | null}
): TextAnchor | null {
  if (start < 0 || end > projection.text.length || start >= end) {
    return null;
  }
  const exact = projection.text.slice(start, end);
  if (exact.trim() === '') {
    return null;
  }
  return {
    v: 1,
    regionId,
    lang,
    start,
    end,
    exact,
    prefix: projection.text.slice(Math.max(0, start - PREFIX_SUFFIX_LENGTH), start),
    suffix: projection.text.slice(end, end + PREFIX_SUFFIX_LENGTH)
  };
}

/**
 * Validate-or-drop: resolves iff the normalized text still reads `exact` at
 * `[start, end)` — never approximated. A drop is not a deletion; the anchor
 * resolves again when the DOM returns to its captured shape.
 */
export function resolveAnchor(
  anchor: TextAnchor,
  projection: TextProjection
): AnnotationRange | null {
  const {start, end, exact} = anchor;
  if (start < 0 || end <= start) {
    return null;
  }
  if (projection.text.slice(start, end) !== exact) {
    return null;
  }
  return {start, end};
}
